import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, MapPin, Factory } from 'lucide-react';
import { Button } from './ui/button';

const HeroSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/3830ms51_uNXNFOB4KU0f72XD71uMr.avif"
          alt="Strawberry Studio Exports Building"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-sans text-sm md:text-base font-medium tracking-[0.3em] uppercase text-white/70 mb-6"
          >
            Manufacturers & Exporters Since 1996
          </motion.p>

          {/* Main Heading */}
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-medium tracking-tight leading-[1.1] text-white mb-8">
            <span className="block">Crafting Quality</span>
            <span className="block text-rose-400">Kidswear & Ladieswear</span>
          </h1>

          {/* Subtitle */}
          <p className="font-sans text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-12 leading-relaxed">
            Supplying to the world's leading fashion brands across UK, Europe, USA & Middle East. 
            350,000+ pieces monthly capacity with complete in-house facilities.
          </p>

          {/* Stats Row */}
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-center"
            >
              <p className="font-serif text-4xl md:text-5xl font-medium text-white">350K+</p>
              <p className="font-sans text-sm text-white/60 uppercase tracking-wider mt-2">Pieces/Month</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="text-center"
            >
              <p className="font-serif text-4xl md:text-5xl font-medium text-white">28+</p>
              <p className="font-sans text-sm text-white/60 uppercase tracking-wider mt-2">Years Experience</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="text-center"
            >
              <p className="font-serif text-4xl md:text-5xl font-medium text-white">10+</p>
              <p className="font-sans text-sm text-white/60 uppercase tracking-wider mt-2">Global Brands</p>
            </motion.div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              data-testid="hero-explore-btn"
              onClick={() => scrollToSection('#products')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-medium transition-all duration-300 hover:scale-105"
            >
              Explore Products
            </Button>
            <Button
              data-testid="hero-contact-btn"
              onClick={() => scrollToSection('#contact')}
              variant="outline"
              className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-medium transition-all duration-300"
            >
              Partner With Us
            </Button>
          </div>
        </motion.div>

        {/* Locations Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-wrap justify-center gap-4"
        >
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md rounded-full px-4 py-2">
            <MapPin size={16} className="text-rose-400" strokeWidth={1.5} />
            <span className="font-sans text-sm text-white/80">Noida, India</span>
          </div>
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md rounded-full px-4 py-2">
            <Factory size={16} className="text-rose-400" strokeWidth={1.5} />
            <span className="font-sans text-sm text-white/80">Bangalore, India</span>
          </div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.6 }}
        className="absolute bottom-8 right-8 hidden md:block"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="flex flex-col items-center gap-2"
        >
          <span className="font-sans text-xs text-white/50 uppercase tracking-wider rotate-90 origin-center translate-y-8">Scroll</span>
          <ArrowDown size={20} className="text-white/50" strokeWidth={1.5} />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
